<template>
  <div class="reporting-section">
    <div class="section-header">
      <i class="material-icons section-icon">compare_arrows</i>
      <h2>{{ $t('dash.reporting.section.comparison.title') }}</h2>
      <span class="page-count">{{ pagesData.length }} pages</span>
      <Tooltip :text="pdfTooltip" :active="!!(prevLoading || concurrentsLoading)" position="bottom">
        <button class="btn-pdf" :disabled="pdfLoading || prevLoading || concurrentsLoading" @click="downloadComparisonPdf">
          <i class="material-icons">{{ pdfLoading ? 'hourglass_empty' : 'picture_as_pdf' }}</i>
          {{ pdfLoading ? '…' : 'PDF' }}
        </button>
      </Tooltip>
    </div>

    <div class="comparison-table">
      <!-- Header row -->
      <div class="cmp-row cmp-head">
        <div class="cmp-metric-label"></div>
        <div class="cmp-page-col" v-for="item in pagesData" :key="item.page.id">
          <div class="page-badge page-badge--clickable" @click="$emit('drill-down', item.page.id)" :title="$t('dash.reporting.see_detail')">
            <img
              v-if="item.page.picture_url"
              :src="item.page.picture_url"
              class="page-avatar"
              alt=""
            >
            <span class="page-name">{{ item.page.name }}</span>
            <span class="network-badge">{{ item.page.network }}</span>
            <span class="drill-hint"><i class="material-icons">open_in_new</i></span>
          </div>
        </div>
        <div class="cmp-page-col cmp-total-col">
          <div class="page-badge">
            <span class="page-name">Total</span>
          </div>
        </div>
      </div>

      <!-- Metric rows -->
      <div class="cmp-row" v-for="metric in metrics" :key="metric.key">
        <div class="cmp-metric-label">{{ metric.label }}</div>
        <div
          class="cmp-page-col"
          v-for="item in pagesData"
          :key="item.page.id"
          :class="{ 'is-best': getBest(metric.key) === item.page.id }"
        >
          <span class="cmp-value">{{ formatValue(getValue(item, metric.key), metric.format) }}</span>
          <span v-if="prevLoading" class="cmp-evol cmp-evol--loading">…</span>
          <span v-else-if="getEvolution(item, metric.key) !== null" class="cmp-evol" :class="evolClass(item, metric.key)">
            <i class="material-icons">{{ getEvolution(item, metric.key) >= 0 ? 'arrow_upward' : 'arrow_downward' }}</i>
            {{ formatEvol(getEvolution(item, metric.key)) }}
          </span>
        </div>
        <div class="cmp-page-col cmp-total-col">
          <span class="cmp-value cmp-total-value">{{ formatValue(getTotal(metric.key), metric.format) }}</span>
          <span v-if="prevLoading" class="cmp-evol cmp-evol--loading">…</span>
          <span v-else-if="getTotalEvolution(metric.key) !== null" class="cmp-evol" :class="totalEvolClass(metric.key)">
            <i class="material-icons">{{ getTotalEvolution(metric.key) >= 0 ? 'arrow_upward' : 'arrow_downward' }}</i>
            {{ formatEvol(getTotalEvolution(metric.key)) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Tooltip from '../tooltip/tooltipInit.vue'

export default {
  name: 'ReportingComparison',
  components: { Tooltip },
  props: {
    pagesData: { type: Array, required: true },
    dateStart: { default: null },
    dateEnd: { default: null },
    sortCriterion: { type: String, default: 'engagement' },
    concurrentData: { type: Object, default: () => ({}) },
    prevLoading: { type: Boolean, default: false },
    concurrentsLoading: { type: Boolean, default: false },
  },
  data() {
    return { pdfLoading: false }
  },
  computed: {
    pdfTooltip() {
      if (this.prevLoading) return this.$t('dash.reporting.section.comparison.pdf_wait_prev')
      if (this.concurrentsLoading) return this.$t('dash.reporting.section.comparison.pdf_wait_concurrent')
      return ''
    },
    metrics() {
      return [
        { key: 'page_fans',       label: this.$t('dash.reporting.followers'),        format: 'number' },
        { key: 'nb_posts',        label: this.$t('dash.reporting.publications'),      format: 'number' },
        { key: 'totalImpressions',label: this.$t('dash.reporting.impression'),        format: 'number' },
        { key: 'totalEngagement', label: this.$t('dash.reporting.interactions'),      format: 'number' },
        { key: 'totalReactions',  label: this.$t('dash.reporting.reaction'),          format: 'number' },
        { key: 'totalComments',   label: this.$t('dash.reporting.comment'),           format: 'number' },
        { key: 'totalShares',     label: this.$t('dash.reporting.share'),             format: 'number' },
        { key: 'engagement_rate', label: this.$t('dash.reporting.engagement_rate'),   format: 'percent' },
      ]
    },
  },
  methods: {
    getValue(item, key) {
      if (!item.stats || !item.stats.page) return 0
      if (key === 'engagement_rate') {
        var eng = item.stats.page.totalEngagement || 0
        var imp = item.stats.page.totalImpressions || 0
        return imp > 0 ? parseFloat((eng / imp * 100).toFixed(2)) : 0
      }
      return item.stats.page[key] || 0
    },
    getPrevValue(item, key) {
      if (!item.stats || !item.stats.page || !item.stats.page.previous_period) return null
      var prev = item.stats.page.previous_period
      return prev[key] != null ? prev[key] : null
    },
    getEvolution(item, key) {
      var cur = this.getValue(item, key)
      var prev = this.getPrevValue(item, key)
      if (prev === null) return null
      if (prev === 0) return cur > 0 ? 100 : 0
      return parseFloat(((cur - prev) / Math.abs(prev) * 100).toFixed(1))
    },
    evolClass(item, key) {
      var e = this.getEvolution(item, key)
      if (e === null || e === 0) return 'cmp-evol--neutral'
      return e > 0 ? 'cmp-evol--up' : 'cmp-evol--down'
    },
    getTotalPrev(key) {
      var hasPrev = this.pagesData.some(item => this.getPrevValue(item, key) !== null)
      if (!hasPrev) return null
      if (key === 'engagement_rate') {
        var totalEng = this.pagesData.reduce((s, item) => s + (this.getPrevValue(item, 'totalEngagement') || 0), 0)
        var totalImp = this.pagesData.reduce((s, item) => s + (this.getPrevValue(item, 'totalImpressions') || 0), 0)
        return totalImp > 0 ? parseFloat((totalEng / totalImp * 100).toFixed(2)) : 0
      }
      return this.pagesData.reduce((s, item) => s + (this.getPrevValue(item, key) || 0), 0)
    },
    getTotalEvolution(key) {
      var cur = this.getTotal(key)
      var prev = this.getTotalPrev(key)
      if (prev === null) return null
      if (prev === 0) return cur > 0 ? 100 : 0
      return parseFloat(((cur - prev) / Math.abs(prev) * 100).toFixed(1))
    },
    totalEvolClass(key) {
      var e = this.getTotalEvolution(key)
      if (e === null || e === 0) return 'cmp-evol--neutral'
      return e > 0 ? 'cmp-evol--up' : 'cmp-evol--down'
    },
    formatEvol(e) {
      if (e === null) return ''
      return (e > 0 ? '+' : '') + e + '%'
    },
    formatValue(val, format) {
      if (format === 'percent') return (val || 0).toFixed(2) + '%'
      return (val || 0).toLocaleString()
    },
    getBest(key) {
      if (!this.pagesData.length) return null
      let best = this.pagesData[0]
      this.pagesData.forEach(item => {
        if (this.getValue(item, key) > this.getValue(best, key)) best = item
      })
      return best.page.id
    },
    getTotal(key) {
      if (key === 'engagement_rate') {
        var totalEng = this.pagesData.reduce((s, item) => s + (this.getValue(item, 'totalEngagement')), 0)
        var totalImp = this.pagesData.reduce((s, item) => s + (this.getValue(item, 'totalImpressions')), 0)
        return totalImp > 0 ? parseFloat((totalEng / totalImp * 100).toFixed(2)) : 0
      }
      return this.pagesData.reduce((s, item) => s + this.getValue(item, key), 0)
    },
    async downloadComparisonPdf() {
      if (this.pdfLoading) return
      this.pdfLoading = true
      try {
        const { VUE_APP_API_HOST } = process.env
        const token = localStorage.getItem('token')
        const locale = this.$i18n && this.$i18n.locale ? this.$i18n.locale : 'fr'
        const resp = await fetch(`${VUE_APP_API_HOST}/reporting/pdf/v2/comparison`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            pagesData: this.pagesData,
            dateStart: this.dateStart,
            dateEnd: this.dateEnd,
            locale,
            sortCriterion: this.sortCriterion,
            concurrentsMap: this.concurrentData && Object.keys(this.concurrentData).length ? this.concurrentData : null,
          }),
        })
        if (!resp.ok) throw new Error('PDF generation failed: ' + resp.status)
        const buf = await resp.arrayBuffer()
        const blob = new Blob([buf], { type: 'application/pdf' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        const period = this.dateStart ? String(this.dateStart) : 'period'
        a.download = `reporting-comparison-${period}.pdf`
        document.body.appendChild(a)
        a.click()
        setTimeout(() => { URL.revokeObjectURL(url); document.body.removeChild(a) }, 1000)
      } catch (e) {
        console.error('[comparison pdf]', e)
      } finally {
        this.pdfLoading = false
      }
    },
  },
}
</script>

<style scoped>
.reporting-section {
  background: #fff;
  border-radius: 5.6px;
  padding: 20px 24px;
  box-shadow: 0 2px 5.6px rgba(107,31,77,0.06);
  border: 1px solid #f1edf5;
}
.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
}
.section-icon { color: #6B1F4D; font-size: 22px; }
.section-header h2 { font-size: 16px; font-weight: 700; color: #121826; margin: 0; }
.page-count {
  margin-left: auto;
  font-size: 12px;
  color: #9ba3b7;
  background: #f1edf5;
  padding: 2px 10px;
  border-radius: 20px;
}
.btn-pdf {
  display: flex;
  align-items: center;
  gap: 5px;
  background: transparent !important;
  color: #6B1F4D !important;
  border: 1.5px solid #6B1F4D !important;
  border-radius: 8px;
  padding: 4px 11px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-pdf:hover:not(:disabled) { background: #6B1F4D; color: #fff; }
.btn-pdf:disabled { opacity: 0.45; cursor: default; }
.btn-pdf .material-icons { font-size: 14px; }

.comparison-table { overflow-x: auto; }
.cmp-row {
  display: flex;
  border-bottom: 1px solid #f9f7fb;
  min-width: 0;
}
.cmp-row:last-child { border-bottom: none; }
.cmp-head { border-bottom: 2px solid #f1edf5; padding-bottom: 10px; }

.cmp-metric-label {
  width: 160px;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  color: #9ba3b7;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 10px 8px 10px 0;
  display: flex;
  align-items: center;
}
.cmp-page-col {
  flex: 1;
  min-width: 120px;
  padding: 10px 8px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.cmp-page-col.is-best .cmp-value {
  color: #6B1F4D;
  font-weight: 700;
}

.page-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.page-badge--clickable {
  cursor: pointer;
  border-radius: 5.6px;
  padding: 4px;
  transition: background 0.15s;
}
.page-badge--clickable:hover { background: #f1edf5; }
.page-badge--clickable:hover .drill-hint { opacity: 1; }
.drill-hint {
  opacity: 0;
  transition: opacity 0.15s;
  display: flex;
  align-items: center;
}
.drill-hint .material-icons { font-size: 13px; color: #6B1F4D; }
.page-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #f1edf5;
}
.page-name {
  font-size: 12px;
  font-weight: 600;
  color: #121826;
  text-align: center;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.network-badge {
  font-size: 10px;
  color: #9ba3b7;
  background: #f1edf5;
  padding: 1px 6px;
  border-radius: 10px;
  text-transform: capitalize;
}
.cmp-value {
  font-size: 14px;
  font-weight: 500;
  color: #121826;
}
.cmp-evol {
  display: inline-flex;
  align-items: center;
  gap: 1px;
  font-size: 10px;
  font-weight: 700;
  border-radius: 10px;
  padding: 1px 5px;
}
.cmp-evol .material-icons { font-size: 10px; }
.cmp-evol--up      { color: #16a34a; background: #f0fdf4; }
.cmp-evol--down    { color: #dc2626; background: #fef2f2; }
.cmp-evol--neutral { color: #9ba3b7; background: #f1edf5; }
.cmp-evol--loading { color: #c4b5d0; background: none; font-size: 11px; }
.cmp-total-col {
  background: #faf8fc;
  border-left: 2px solid #f1edf5;
}
.cmp-head .cmp-total-col {
  display: flex;
  align-items: center;
  justify-content: center;
}
.cmp-total-value {
  font-weight: 700;
  color: #6B1F4D;
}
</style>
